import { Injectable, OnDestroy, inject, signal } from '@angular/core';

import { Clock } from '../../runtime/clock/clock';
import { TELEMETRY_SINK_FACTORY } from '../../runtime/telemetry/app-insights.sink';
import { BreadcrumbBuffer } from '../../runtime/telemetry/breadcrumb-buffer';
import {
  BreadcrumbCategory, DiagnosticVerbosity, ErrorSource, ShutdownReason, StorageFailure
} from '../../runtime/telemetry/enums';
import { TelemetrySink } from '../../runtime/telemetry/interfaces';
import {
  DiagnosticErrorModel, HeartbeatPayloadModel, SafeProperties, SessionRecordModel, TelemetryProperties
} from '../../runtime/telemetry/models';
import { SessionStoreService } from '../../runtime/telemetry/session-store.service';
import { TelemetryScrubberService } from '../../runtime/telemetry/telemetry-scrubber.service';
import { DataCenterPrefix } from '../enums';
import { APP_CONFIG } from '../injection-tokens/app-config';

declare const cubigoAppVersion: { build?: string } | undefined;
declare const deviceInformation: {
  id?: string
  androidOsVersion?: string
  appVersionCode?: string
  appVersionName?: string
} | undefined;

@Injectable({ providedIn: 'root' })
export class DsLoggingService implements OnDestroy {
  private readonly config = inject(APP_CONFIG, { optional: true });
  private readonly sessionStore = inject(SessionStoreService);
  private readonly sinkFactory = inject(TELEMETRY_SINK_FACTORY);
  private readonly clock = inject(Clock);
  private readonly scrubber = inject(TelemetryScrubberService);
  private readonly breadcrumbs = new BreadcrumbBuffer(this.scrubber);
  private readonly pending: { name: string; properties: SafeProperties }[] = [];
  private sink?: TelemetrySink;
  private session?: SessionRecordModel;
  private version?: string;
  private instrumentationKey?: string;
  private reportedFailure?: StorageFailure;
  private readonly diagnosticVerbosity = signal<DiagnosticVerbosity>(
    this.config?.diagnostics?.defaultVerbosity ?? DiagnosticVerbosity.Verbose
  );
  private readonly errors = signal<readonly DiagnosticErrorModel[]>([]);

  public readonly storageFailure = this.sessionStore.storageFailure;
  public readonly verbosity = this.diagnosticVerbosity.asReadonly();
  public readonly recentErrors = this.errors.asReadonly();
  private readonly pagehide = (): void => this.markCleanShutdown(ShutdownReason.Pagehide);

  public setVerbosity(level: DiagnosticVerbosity): void {
    this.diagnosticVerbosity.set(level);
    this.sessionStore.writeVerbosity(level);
    this.reportStorageFailure();
  }

  public get displayId(): string {
    return this.session?.displayId ?? '';
  }

  public get deviceId(): string | undefined {
    return typeof deviceInformation === 'undefined' ? undefined : deviceInformation?.id;
  }

  public get appVersion(): string | undefined {
    return this.version;
  }

  public set appVersion(version: string) {
    this.version = typeof this.scrubber.scrub({ appVersion: version })['appVersion'] === 'string' ? version : undefined;
  }

  public get userAgent(): string | null {
    const build = this.buildVersion();

    if (!build) {
      return null;
    }

    const native = typeof deviceInformation === 'undefined' ? undefined : deviceInformation;
    const versions = native && [native.androidOsVersion, native.appVersionCode, native.appVersionName];

    return versions?.every(value => typeof value === 'string' && /^\d+(?:\.\d+){0,3}$/.test(value))
      ? `Web-DS/${build} (Android/${versions[0]}; CodeVersion/${versions[1]}; VersionName/${versions[2]})`
      : `Web-DS/${build}`;
  }

  public ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.pagehide);
    }

    this.sink?.dispose();
    this.sink = undefined;
  }

  public beginSession(): void {
    if (this.session && !this.session.closedCleanly) {
      return;
    }

    const previous = this.sessionStore.readSession();
    const override = this.sessionStore.readVerbosity();
    const now = this.clock.now().toEpochMilli();

    if (override) {
      this.diagnosticVerbosity.set(override);
    }

    this.breadcrumbs.clear();
    this.session = {
      schema: 1, displayId: this.sessionStore.displayId(), sessionId: this.sessionStore.createTelemetryId(),
      startedAt: now, updatedAt: now, closedCleanly: false, snapshot: {}, breadcrumbs: []
    };

    if (previous && !previous.closedCleanly) {
      this.reportUncleanRestart(previous);
    }

    this.breadcrumb(BreadcrumbCategory.Lifecycle, 'session_started');
    this.snapshotSession();

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.pagehide);
    }
  }

  public breadcrumb(category: BreadcrumbCategory, message: string, properties: TelemetryProperties = {}): void {
    if (!this.session || this.session.closedCleanly) {
      return;
    }

    this.breadcrumbs.add(this.clock.now().toEpochMilli() - this.session.startedAt, category, message, properties);

    if (category === BreadcrumbCategory.Auth) {
      this.snapshotSession();
    }
  }

  public snapshotSession(properties: TelemetryProperties | HeartbeatPayloadModel = {}): void {
    if (!this.session) {
      return;
    }

    this.session = {
      ...this.session, updatedAt: this.clock.now().toEpochMilli(),
      snapshot: this.scrubber.scrub({ ...this.session.snapshot, ...properties }),
      breadcrumbs: this.breadcrumbs.snapshot()
    };
    this.sessionStore.writeSession(this.session);
    this.reportStorageFailure();
  }

  public markCleanShutdown(reason: ShutdownReason): void {
    if (!this.session || this.session.closedCleanly) {
      return;
    }

    this.breadcrumb(BreadcrumbCategory.Lifecycle, 'session_closed', { reason });
    this.session = { ...this.session, closedCleanly: true, reason };
    this.snapshotSession();
  }

  public trackHeartbeat(payload: HeartbeatPayloadModel): void {
    if (!this.session || this.session.closedCleanly) {
      return;
    }

    const properties = this.scrubber.scrubHeartbeat({ ...payload });
    this.snapshotSession(properties);
    this.emit('heartbeat', properties);
  }

  public logError(error: unknown, source?: ErrorSource): void {
    const properties = {
      classification: this.scrubber.classifyError(error), httpStatus: this.scrubber.record(error)['status'], source
    };
    this.errors.update(errors => [
      ...errors, { classification: properties.classification, timestamp: this.clock.now().toEpochMilli() }
    ].slice(-5));
    this.breadcrumb(BreadcrumbCategory.Lifecycle, 'error', properties);
    // Name and frames go on the incident only; a breadcrumb trail of stacks would crowd out the events around it.
    this.emitIncident('error', {
      ...properties, errorName: this.scrubber.errorName(error), frames: this.scrubber.errorFrames(error)
    });
  }

  public logEffectsError(_exception: string, _action: string): void {
    this.breadcrumb(BreadcrumbCategory.Lifecycle, 'error', { context: 'effects' });
    this.emitIncident('error', { classification: 'error', context: 'effects' });
  }

  public getTraceId(): string | undefined {
    return this.session?.sessionId;
  }

  public trackPageView(name: string, eventProperties: TelemetryProperties = {}): void {
    const event = name === 'Go to next slide' ? 'slide_changed' : 'page_view';
    this.breadcrumb(BreadcrumbCategory.Playback, event, eventProperties);
    this.emit(event, eventProperties);
  }

  public trackEvent(name: string, eventProperties: TelemetryProperties = {}): void {
    const event = this.scrubber.safeEventName(name);
    const category: BreadcrumbCategory = event.startsWith('auth_') || event.startsWith('refresh_')
      || ['logout_performed', 'credentials_revoked', 'login_succeeded'].includes(event) ? BreadcrumbCategory.Auth
      : event.startsWith('feed_') ? BreadcrumbCategory.Content
        : event.startsWith('network_') ? BreadcrumbCategory.Net : BreadcrumbCategory.Lifecycle;
    this.breadcrumb(category, event, eventProperties);

    if (['error', 'logout_performed', 'credentials_revoked', 'playback_stalled', 'slide_overrun', 'memory_pressure_reload']
      .includes(event)) {
      this.emitIncident(event, eventProperties);
    } else {
      this.emit(event, eventProperties);
    }
  }

  public trackTrace(message: string, properties?: { [name: string]: string }): void {
    this.breadcrumb(BreadcrumbCategory.Lifecycle, this.scrubber.safeEventName(message, 'trace'), properties);
    this.emit(this.scrubber.safeEventName(message, 'trace'), properties ?? {});
  }

  public clearAuthenticatedUserContext(): void {
    // Person-level context is intentionally never set on either sink.
  }

  public setAuthenticatedUserContext(_authenticatedUserId: string): void {
    // Retained for older consumers; raw account identifiers must not reach telemetry.
  }

  public setInstrumentationKeyOnRegion(region: string): void {
    const keys = this.config?.appInsights;
    const key = region === DataCenterPrefix.EU ? keys?.instrumentationKeyEu
      : region === DataCenterPrefix.CA ? keys?.instrumentationKeyCa : keys?.instrumentationKey;
    if (!key || key === this.instrumentationKey) {
      return;
    }

    this.sink?.dispose();
    this.sink = this.sinkFactory(key);
    this.instrumentationKey = key;

    for (const event of this.pending.splice(0)) {
      this.sink.event(event.name, event.properties);
    }
  }

  private emitIncident(name: string, properties: TelemetryProperties): void {
    this.emit(name, { ...properties, breadcrumbs: this.breadcrumbs.snapshot() });
    this.snapshotSession();
  }

  private emit(name: string, properties: TelemetryProperties): void {
    const incidents = ['heartbeat', 'error', 'logout_performed', 'credentials_revoked', 'display_unclean_restart',
      'storage_failure', 'playback_stalled', 'slide_overrun', 'feed_failed', 'memory_pressure_reload'];
    const suppressed = this.verbosity() === DiagnosticVerbosity.Normal && !incidents.includes(name);
    const traceOnly = name === 'trace' && this.verbosity() !== DiagnosticVerbosity.Trace;

    if (suppressed || traceOnly) {
      return;
    }

    const safeProperties = this.scrubber.scrub({
      ...properties, dsWeb: true, appVersion: this.version, Build: this.buildVersion(),
      displayId: this.session?.displayId, sessionId: this.session?.sessionId
    });

    if (this.sink) {
      this.sink.event(this.scrubber.safeEventName(name), safeProperties);
    } else {
      if (this.pending.length === 20) {
        this.pending.shift();
      }

      this.pending.push({ name: this.scrubber.safeEventName(name), properties: safeProperties });
    }
  }

  private reportUncleanRestart(previous: SessionRecordModel): void {
    const snapshot = previous.snapshot;
    this.emit('display_unclean_restart', {
      previousUptimeMs: snapshot['uptimeMs'] ?? Math.max(0, previous.updatedAt - previous.startedAt),
      lastState: snapshot['state'], lastSlideKind: snapshot['currentSlideKind'],
      lastHeapMb: snapshot['usedJsHeapMb'], heapLimitMb: snapshot['heapLimitMb'],
      suspectedOom: typeof snapshot['usedJsHeapMb'] === 'number' && typeof snapshot['heapLimitMb'] === 'number'
        && snapshot['heapLimitMb'] > 0 && snapshot['usedJsHeapMb'] > 0.8 * snapshot['heapLimitMb'],
      activeTimersAtDeath: snapshot['activeTimers'], consecutiveFeedFailures: snapshot['consecutiveFeedFailures'],
      breadcrumbs: previous.breadcrumbs
    });
  }

  private reportStorageFailure(): void {
    const failure = this.storageFailure();

    if (failure && failure !== this.reportedFailure) {
      this.reportedFailure = failure;
      this.emit('storage_failure', { storageFailure: failure });
    }
  }

  private buildVersion(): string | undefined {
    const build = typeof cubigoAppVersion === 'undefined' ? undefined : cubigoAppVersion?.build;
    const sanitized = this.scrubber.scrub({ Build: build })['Build'];

    return typeof sanitized === 'string' ? sanitized : undefined;
  }
}
